import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

const COOKIE = "cc_consent";
const GA_ID = "G-J2Y4MP8KVM";

function getConsent() {
  const m = document.cookie.match(new RegExp(`(^| )${COOKIE}=([^;]+)`));
  return m ? decodeURIComponent(m[2]) : "unknown";
}

function setConsent(val) {
  const maxAge = 60 * 60 * 24 * 365; // 1 year
  const parts = [
    `${COOKIE}=${encodeURIComponent(val)}`,
    "Path=/",
    `Max-Age=${maxAge}`,
  ];

  // In prod, share across cheerclip.com + app.cheerclip.com
  const host = typeof window !== "undefined" ? window.location.hostname : "";
  const isLocal = host === "localhost" || host === "127.0.0.1";
  if (!isLocal && host.endsWith("cheerclip.com")) {
    parts.push("Domain=.cheerclip.com", "SameSite=Lax", "Secure");
  } else {
    // local dev: cookies with Domain/Secure often won’t set; keep it simple
    parts.push("SameSite=Lax");
  }

  document.cookie = parts.join("; ");
}

function loadGA(measurementId) {
  if (document.getElementById("ga4-src")) return;

  window.dataLayer = window.dataLayer || [];
  window.gtag = window.gtag || function gtag(){ window.dataLayer.push(arguments); };

  const s = document.createElement("script");
  s.async = true;
  s.id = "ga4-src";
  s.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
  document.head.appendChild(s);

  window.gtag("js", new Date());
  window.gtag("config", measurementId);
}

export default function SimpleConsent() {
  const { pathname } = useLocation();
  const [status, setStatus] = useState("unknown");

  // /get is a flyer QR redirect — no analytics cookies, no banner flash
  const skipConsent = pathname === "/get";

  useEffect(() => {
    if (skipConsent) return;
    const s = getConsent();
    setStatus(s);
    if (s === "accepted") loadGA(GA_ID);
  }, [skipConsent]);

  const accept = () => {
    setConsent("accepted");
    setStatus("accepted");
    loadGA(GA_ID);
  };

  const reject = () => {
    setConsent("rejected");
    setStatus("rejected");
  };

  if (skipConsent || status !== "unknown") return null;

return (
    <>
      {/* Responsive styles for mobile */}
      <style>{`
        .cc-banner { 
          position: fixed; left: 0; right: 0; bottom: 0; z-index: 9999;
          padding: 12px 16px calc(env(safe-area-inset-bottom, 0) + 12px);
          background: rgba(0,0,0,.9); color: #fff;
        }
        .cc-wrap {
          max-width: 960px; margin: 0 auto; display: flex; gap: 12px; 
          align-items: center; flex-wrap: wrap;
        }
        .cc-text {
          flex: 1; 
          /* scales between 14px and 18px depending on viewport */
          font-size: clamp(14px, 2.5vw, 18px);
          line-height: 1.4;
        }
        .cc-actions {
          display: flex; gap: 8px;
        }
        .cc-btn {
          border: 0; border-radius: 10px; cursor: pointer;
          padding: 14px 16px; /* bigger tap target on mobile */
          font-size: clamp(14px, 2.8vw, 16px);
          width: auto;
        }
        .cc-btn-primary { background: #319795; color: #fff; }
        .cc-btn-secondary { background: #333; color: #fff; }

        /* Stack on small screens */
        @media (max-width: 600px) {
          .cc-wrap { flex-direction: column; align-items: stretch; }
          .cc-actions { flex-direction: column; width: 100%; }
          .cc-btn { width: 100%; }
        }

        /* Focus states for accessibility */
        .cc-btn:focus-visible { outline: 2px solid #fff; outline-offset: 2px; }
      `}</style>

      <div className="cc-banner" role="region" aria-label="Cookie consent">
        <div className="cc-wrap">
          <p className="cc-text">
            We use cookies to enhance your experience, provide essential features, and help us
            understand how our site is used. You can accept all cookies or continue with essential only.
          </p>
          <div className="cc-actions" role="group" aria-label="Cookie choices">
            <button onClick={reject} className="cc-btn cc-btn-secondary">
              Essential only
            </button>
            <button onClick={accept} className="cc-btn cc-btn-primary">
              Accept all
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

